import { useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp } from "lucide-react";

const fmt = (n) => "$" + Math.round(n).toLocaleString();

export default function RoiCalculator() {
  const [visitors, setVisitors] = useState(400);
  const [jobValue, setJobValue] = useState(3500);

  const currentLeads = visitors * 0.015;
  const newLeads = visitors * 0.045;
  const extraJobs = (newLeads - currentLeads) * 0.25;
  const monthlyGain = extraJobs * jobValue;
  const yearlyGain = monthlyGain * 12;

  return (
    <section id="roi" data-testid="agency-roi-calculator" className="py-28 lg:py-36">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-gold/80 mb-4">The Math</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-[#F8F6F0] tracking-tight">
            What is a better website <span className="gold-text italic">actually worth?</span>
          </h2>
          <p className="mt-6 text-zinc-400 leading-relaxed max-w-md">
            Most trade sites convert under 2%. Ours are engineered for 4.5%+.
            Drag the sliders to see what that gap means for your bottom line.
          </p>

          <div className="mt-10 space-y-9">
            <div>
              <div className="flex justify-between mb-3">
                <label className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">Monthly site visitors</label>
                <span className="font-serif text-xl text-gold" data-testid="roi-visitors-value">{visitors.toLocaleString()}</span>
              </div>
              <input
                type="range" min="100" max="5000" step="50" value={visitors}
                data-testid="roi-visitors-slider"
                onChange={(e) => setVisitors(+e.target.value)}
                className="luxury-slider w-full"
                style={{ "--fill": `${((visitors - 100) / 4900) * 100}%` }}
              />
            </div>
            <div>
              <div className="flex justify-between mb-3">
                <label className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">Average job value</label>
                <span className="font-serif text-xl text-gold" data-testid="roi-jobvalue-value">{fmt(jobValue)}</span>
              </div>
              <input
                type="range" min="500" max="25000" step="250" value={jobValue}
                data-testid="roi-jobvalue-slider"
                onChange={(e) => setJobValue(+e.target.value)}
                className="luxury-slider w-full"
                style={{ "--fill": `${((jobValue - 500) / 24500) * 100}%` }}
              />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="trace-border rounded-3xl border gold-border-subtle bg-surface/70 p-9 lg:p-12"
          data-testid="roi-results-panel"
        >
          <div className="flex items-center gap-3 mb-8">
            <TrendingUp size={20} className="text-gold" />
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-400">Projected Lift with Opulent</span>
          </div>
          <div className="space-y-7">
            <div>
              <div className="font-serif text-5xl lg:text-6xl gold-text" data-testid="roi-monthly-gain">{fmt(monthlyGain)}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-2">Additional monthly revenue</div>
            </div>
            <div className="h-px bg-gradient-to-r from-gold/30 to-transparent" />
            <div className="grid grid-cols-2 gap-6">
              <div>
                <div className="font-serif text-3xl text-[#F8F6F0]" data-testid="roi-yearly-gain">{fmt(yearlyGain)}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-2">Per year</div>
              </div>
              <div>
                <div className="font-serif text-3xl text-[#F8F6F0]" data-testid="roi-extra-leads">{extraJobs.toFixed(1)}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-2">Extra jobs / month</div>
              </div>
            </div>
          </div>
          <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-600 leading-relaxed">
            Illustrative projection based on 4.5% conversion vs. 1.5% industry average and a 25% lead-to-job close rate.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
